import { Box, ButtonBase, LinearProgress, Popover, Typography } from "@mui/material";
import { useState } from "react";
import { alpha } from "@mui/material/styles";
import { StatusBadge } from "@/shared/components";
import { layoutTokens } from "@/shared/theme";
import { focusName, focusStatusCopy, focusWhyCopy } from "../focus-copy";
import type { FocusCandidate, FocusProgress } from "../patterns.api";

interface TrackedPatternCardProps {
  readonly pattern: FocusCandidate;
  readonly cohort: string;
  readonly isFocus: boolean;
  /** This pattern week by week, oldest first. */
  readonly weeks?: FocusProgress["weeks"];
  /** Opens this pattern's evidence; omitted for clock patterns, which have no motif evidence card. */
  readonly onOpen?: () => void;
}

/** One tracked pattern: what it is, why it is tracked, and where its measurement stands. */
const share = (missed: number, games: number) => `${Math.round((100 * missed) / games)}% (${missed} of ${games})`;
const shortDate = (weekStart: string) =>
  new Date(`${weekStart}T00:00:00Z`).toLocaleDateString("en", { month: "short", day: "numeric", timeZone: "UTC" });

type Week = FocusProgress["weeks"][number];
const games = (count: number) => `${count} ${count === 1 ? "game" : "games"}`;
const weekCopy = (week: Week, name: string, type: FocusCandidate["type"]) =>
  week.games === 0
    ? "No counted games this week."
    : type === "clock"
      ? week.missed === 0
        ? `Clean week: no ${name} mistakes in ${games(week.games)}.`
        : `${name[0].toUpperCase()}${name.slice(1)} led to a mistake in ${week.missed} of ${games(week.games)}.`
      : week.missed === 0
        ? `Clean week: no ${name} missed in ${games(week.games)}.`
        : `You missed ${name} in ${week.missed} of ${games(week.games)}.`;

/** One box per week (games with a miss / games played), each explained by a popover, plus an earlier-versus-recent comparison. */
const WeeklyStrip = ({
  weeks,
  name,
  type,
}: {
  weeks: FocusProgress["weeks"];
  name: string;
  type: FocusCandidate["type"];
}) => {
  const [open, setOpen] = useState<{ anchor: HTMLElement; week: Week } | null>(null);
  const total = (rows: FocusProgress["weeks"]) => ({
    games: rows.reduce((sum, week) => sum + week.games, 0),
    missed: rows.reduce((sum, week) => sum + week.missed, 0),
  });
  const all = total(weeks);
  if (all.games === 0) return null;
  const half = Math.floor(weeks.length / 2);
  const earlier = total(weeks.slice(0, half));
  const recent = total(weeks.slice(half));
  const summary = `Last ${weeks.length} weeks: missed in ${all.missed} of ${all.games} counted games`;
  return (
    <Box sx={{ borderTop: layoutTokens.dividerBorder, borderColor: "divider", pt: 1 }}>
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", fontWeight: "fontWeightBold" }}>
        LAST {weeks.length} WEEKS
      </Typography>
      {earlier.games > 0 && recent.games > 0 ? (
        <Typography variant="body2">
          Earlier {half} weeks: {share(earlier.missed, earlier.games)} → recent {weeks.length - half} weeks:{" "}
          {share(recent.missed, recent.games)}
        </Typography>
      ) : (
        <Typography variant="body2">Missed in {share(all.missed, all.games)} of your games.</Typography>
      )}
      <Box
        role="group"
        aria-label={summary}
        sx={{ display: "grid", gap: "2px", gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))`, mt: 0.75 }}
      >
        {weeks.map((week) => (
          <ButtonBase
            key={week.weekStart}
            {...(week.games === 0 ? { "data-week-empty": true } : { "data-week-rate": true })}
            aria-label={`Week of ${shortDate(week.weekStart)}: ${weekCopy(week, name, type)}`}
            onClick={(event) => {
              // The card itself opens the pattern; a week box only explains itself.
              event.stopPropagation();
              setOpen({ anchor: event.currentTarget, week });
            }}
            sx={{
              bgcolor: (theme) =>
                week.games === 0
                  ? "transparent"
                  : week.missed
                    ? alpha(theme.palette.secondary.main, 0.3 + (0.7 * week.missed) / week.games)
                    : alpha(theme.palette.success.main, 0.3),
              border: week.games === 0 ? "1px dashed" : "none",
              borderColor: "divider",
              color: week.games === 0 ? "text.disabled" : "text.primary",
              fontSize: 10,
              fontWeight: "fontWeightBold",
              height: 24,
              overflow: "hidden",
              whiteSpace: "nowrap",
              "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main" },
            }}
          >
            {week.games === 0 ? "–" : `${week.missed}/${week.games}`}
          </ButtonBase>
        ))}
      </Box>
      <Box sx={{ color: "text.secondary", display: "flex", fontSize: 10, justifyContent: "space-between", mt: 0.25 }}>
        <span>{shortDate(weeks[0].weekStart)}</span>
        <span>This week</span>
      </Box>
      <Popover
        open={open != null}
        anchorEl={open?.anchor}
        onClose={() => setOpen(null)}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        transformOrigin={{ horizontal: "center", vertical: "top" }}
        slotProps={{
          paper: {
            role: "dialog",
            "aria-label": open ? `Week of ${shortDate(open.week.weekStart)}` : undefined,
            sx: {
              border: layoutTokens.dividerBorder,
              borderColor: "divider",
              borderRadius: 0,
              boxShadow: 0,
              maxWidth: layoutTokens.compactMaxWidth,
              mt: 0.5,
              p: 1.5,
            },
          },
        }}
      >
        {open && (
          <Box>
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: "fontWeightBold" }}>
              WEEK OF {shortDate(open.week.weekStart).toUpperCase()}
            </Typography>
            <Typography variant="body2">{weekCopy(open.week, name, type)}</Typography>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export const TrackedPatternCard = ({ pattern, cohort, isFocus, weeks, onOpen }: TrackedPatternCardProps) => {
  const measuring = ["accepted", "pending", "inconclusive"].includes(pattern.status);
  return (
    <ButtonBase
      component="article"
      disabled={!onOpen}
      onClick={onOpen}
      aria-label={`${focusName(pattern)}: ${focusStatusCopy(pattern, cohort)}`}
      sx={{
        alignItems: "stretch",
        border: isFocus ? layoutTokens.structuralBorder : layoutTokens.dividerBorder,
        borderColor: isFocus ? "secondary.main" : "divider",
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        p: 1.5,
        textAlign: "left",
        width: "100%",
        "&:hover": { bgcolor: onOpen ? "action.hover" : undefined },
        "&.Mui-focusVisible": { outline: `${layoutTokens.structuralBorder}px solid`, outlineColor: "primary.main" },
        "&.Mui-disabled": { color: "text.primary" },
      }}
    >
      <Box sx={{ alignItems: "flex-start", display: "flex", gap: 1, justifyContent: "space-between" }}>
        <Typography component="h3" sx={{ fontWeight: "fontWeightBold", overflowWrap: "anywhere" }}>
          {focusName(pattern)}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, justifyContent: "flex-end" }}>
          {isFocus && <StatusBadge label="STUDY FOCUS" tone="highlight" />}
          {pattern.status === "improved" && <StatusBadge label="IMPROVED" tone="active" />}
          {pattern.status === "regressed" && <StatusBadge label="NOT YET" tone="missed" />}
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {focusWhyCopy(pattern, cohort)}
      </Typography>
      <Typography variant="body2">{focusStatusCopy(pattern, cohort)}</Typography>
      {measuring && (
        <LinearProgress
          variant="determinate"
          aria-hidden
          value={(100 * pattern.measuredGames) / Math.max(1, pattern.windowGames)}
          sx={{ borderRadius: 0, height: 4 }}
        />
      )}
      {weeks && <WeeklyStrip weeks={weeks} name={focusName(pattern).toLowerCase()} type={pattern.type} />}
      {pattern.previousStatus && (
        <Typography variant="caption" color="text.secondary">
          Last cycle: {pattern.previousStatus === "improved" ? "improved" : "not improved"}.
        </Typography>
      )}
    </ButtonBase>
  );
};
